import { useMemo, useState } from 'react';

import { CarePlan } from '~clients/models';

interface CarePlanSelection {
  sortedPlans: CarePlan[];
  selectedPlan?: CarePlan;
  selectPlan: (planId: string) => void;
  activeTab: number;
  onTabChange: (index: number) => void;
}

// Newest plan first: start_date desc with nulls last, created_at desc tiebreak,
// then id for a deterministic order.
const comparePlans = (a: CarePlan, b: CarePlan): number => {
  if (a.start_date !== b.start_date) {
    if (!a.start_date) return 1;
    if (!b.start_date) return -1;
    return b.start_date.localeCompare(a.start_date);
  }
  if (a.created_at !== b.created_at) {
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return b.created_at.localeCompare(a.created_at);
  }
  return a.id.localeCompare(b.id);
};

/**
 * Owns the sidebar plan selection and its coupling to the tab shell: selecting a
 * plan always resets the active tab to Overview (index 0). Selection falls back to
 * the first sorted plan when nothing is selected (auto-select on load) or when the
 * selected plan disappears from the list (e.g. removed by a refetch).
 *
 * initialPlanId seeds the selection for deep links (/clients/care-plans/:plan_id);
 * subsequent selection is local state and does not update the URL.
 */
export const useCarePlanSelection = (plans?: CarePlan[], initialPlanId?: string): CarePlanSelection => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(initialPlanId);
  const [activeTab, setActiveTab] = useState(0);

  const sortedPlans = useMemo(() => [...(plans ?? [])].sort(comparePlans), [plans]);

  const selectedPlan = sortedPlans.find(({ id }) => id === selectedPlanId) ?? sortedPlans[0];

  const selectPlan = (planId: string) => {
    if (planId === selectedPlan?.id) return;
    setSelectedPlanId(planId);
    setActiveTab(0);
  };

  return { sortedPlans, selectedPlan, selectPlan, activeTab, onTabChange: setActiveTab };
};
